import { CUSTOMISED_URL_MAP, getValFromObject } from "../../../../config/FeatureDashboardVarMap";
import { getFeatureAssetBaseUrl } from "../../../../helpers/FeatureAssets";
import axios from "axios";
import { listCampaignAssetsRoute, filesRoute } from "../../../../api/routes";
import { shouldValueBeStored } from "../../../../helpers/FeatureControlsHelper";
import { blankImageData } from "../../../../containers/TimelineEditor/slideout-panel/image-uploader/ImageUploaderContent";

export const customisationSaverHook = {};
export const triggerSaveFeatureData = (featureId, customisationData = null, featureData = null) => {
    if (!featureId) return;
    if (customisationSaverHook && typeof customisationSaverHook.saveFeatureCustomisation === 'function') {
        // console.log('Triggering save feature data from helper: ', featureId, featureData, campaignData);
        if (featureData === null && customisationSaverHook.campaignData) {
            featureData = customisationSaverHook?.campaignData?.features?.[featureId] ?? null;
        }
        if (customisationData === null && customisationSaverHook.customisationVars) {
            customisationData = customisationSaverHook.customisationVars.current[featureId] ?? null;
        }
        customisationSaverHook.saveFeatureCustomisation(featureId, featureData, customisationData);
    }
}

export const undoRedoHook = {};

export const findFeatureData = (campaignData, featureId) => {
    // console.log('Finding feature data from campaign data: ', campaignData);
    if (campaignData && campaignData.features) {
        for (let i = 0; i < campaignData.features.length; i++) {
            if (campaignData.features[i]._id === featureId) {
                return campaignData.features[i];
            }
        }
    }
    return null;
}

/**
 * Get list of campaign assets from the backend by type
 * @param {string} campaignId - The campaign ID
 * @param {string} assetType - The asset type to filter by ('images', 'audio', 'videos', 'fonts')
 * @returns {Promise<Array>} Promise that resolves to array of filtered assets
 */
export const getCampaignAssets = async (campaignId, assetType = null) => {
    if (!campaignId) {
        console.warn('getCampaignAssets: No campaign ID provided');
        return [];
    }

    try {
        const response = await axios.get(listCampaignAssetsRoute, {
            params: { campaignID: campaignId },
            withCredentials: true
        });
        
        if (response.data.success) {
            let assets = response.data.assets.map(asset => {
                const filename = asset.key.split('/').pop();
                // getFeatureAssetBaseUrl returns "{cloudfront|api}/feature-assets/"
                // The S3 key already starts with "feature-assets/" so strip that prefix to avoid duplication
                const assetUrl = `${getFeatureAssetBaseUrl(false)}${asset.key.replace(/^feature-assets\//, '')}`;
                return {
                    filename,
                    fullPath: asset.key,
                    url: assetUrl,
                    name: filename,
                    type: asset.type
                };
            });

            // Filter by asset type if specified
            if (assetType) {
                assets = assets.filter(asset => asset.type === assetType);
            }

            return assets;
        } else {
            console.error('getCampaignAssets: API returned error:', response.data.message);
            return [];
        }
    } catch (error) {
        console.error('getCampaignAssets: Failed to fetch campaign assets:', error);
        return [];
    }
};

/**
 * Get list of all campaign images from the backend
 * @param {string} campaignId - The campaign ID
 * @returns {Promise<Array>} Promise that resolves to array of image assets
 */
export const getCampaignImages = async (campaignId) => {
    return await getCampaignAssets(campaignId, 'images');
};
/**
 * Get list of all campaign audio files from the backend
 * @param {string} campaignId - The campaign ID
 * @returns {Promise<Array>} Promise that resolves to array of audio assets
 */
export const getCampaignAudio = async (campaignId) => {
    return await getCampaignAssets(campaignId, 'audio');
};
/**
 * Get list of all campaign videos from the backend
 * @param {string} campaignId - The campaign ID
 * @returns {Promise<Array>} Promise that resolves to array of video assets
 */
export const getCampaignVideos = async (campaignId) => {
    return await getCampaignAssets(campaignId, 'videos');
};

export const fixSrcUrls = (featureData, featureId, campaignId, customisationDataIn = null, dataToFix = null) => {
    const customisationData = customisationDataIn || featureData?.feature?.customisation;
    if (!customisationData) return; // guard: feature library ref may not be populated
    // console.log('FIX URL', featureData, customisationData, dataToFix);
    for (let i = 0; i < customisationData.length; i++) {
        // console.log('i loop: ', i, customisationData[i], featureId);
        const rootVal = dataToFix; // ?? customisationVarsRef.current.current[featureId];
        const tabKey = customisationData[i].tabKey;
        // console.log('rootVal, tabKey: ', rootVal, tabKey);
        // console.log('-----');
        for (let j = 0; j < customisationData[i].options.length; j++) {
            // console.log('j loop: ', j);
            const varKey = customisationData[i].options[j].varKey;
            // console.log('varKey: ', varKey);
            // console.log('-----');
            for (let k = 0; k < customisationData[i].options[j].variables.length; k++) {
                // console.log('k loop: ', k);
                const varName = customisationData[i].options[j].variables[k].varName;
                // console.log('varName: ', varName);
                // console.log('-----');
                for (let prop in customisationData[i].options[j].variables[k].valSchema) {
                    // console.log('prop loop: ', prop);
                    const type = customisationData[i].options[j].variables[k].valSchema[prop].type.toLowerCase();
                    // console.log('type: ', type);
                    // console.log('-----');
                    if (CUSTOMISED_URL_MAP.hasOwnProperty(type)) {
                        // console.log('We need to generate a Url here!');
                        // We found one we need to map to a url!
                        const vals = getValFromObject(rootVal[tabKey], varKey, varName);
                        // console.log('vals: ', vals);
                        // console.log('-----');
                        if (vals && vals.length) {
                            for (let l = 0; l < vals.length; l++) {
                                // console.log('vals loop: ', l);

                                // Reformat legacy string based filenames to be objects...
                                // SrcUrl has to be part of the object, meaning all feature customisations are going to break...
                                let filename = vals[l][prop];
                                if (typeof filename === 'string' || typeof filename === 'undefined' || filename === null) {
                                    filename = { filename: vals[l][prop] };
                                    vals[l][prop] = filename;
                                    filename.isDefault = vals[l].isDefault;
                                }

                                // console.log('Filename: ', filename);
                                if (filename.srcUrl && typeof filename.srcUrl === 'string' && filename.srcUrl.startsWith('blob:')) {
                                    // Do nothing...
                                } else {
                                    const srcUrl = filename.filename === blankImageData.filename ? blankImageData.srcUrl : 
                                        (
                                            (typeof filename.filename === 'undefined' || filename.filename === null || filename.filename === '') ? null :
                                            (filename.isDefault ? getFeatureAssetBaseUrl(true) + featureData.feature.defaultAssetFolder : getFeatureAssetBaseUrl(false)
                                                + 'campaigns/' + (campaignId /* || campaignContext.campaignData._id */))
                                            + '/' + CUSTOMISED_URL_MAP[type].dir
                                            + '/' + filename.filename
                                        );

                                    // console.log('srcUrl: ', srcUrl);
                                    filename.srcUrl = srcUrl;
                                }
                                filename.originalFile = filename.originalFile || filename.filename;
                                filename.type = CUSTOMISED_URL_MAP[type].dir;
                                // console.log( filename.srcUrl);
                            }
                        }

                        // console.log('Urls generated for: ', vals);
                    }
                }
            }
        }
    }
}

export const addVal = (valsArray, varSchema, featureData, forceUpdateFunc = null, node = null) => {
    console.log('--- Adding vals: ', valsArray, varSchema, featureData);
    // if (validateLastVal(valsArray)) {
        const newValObj = {};
        const templateEntry = Array.isArray(valsArray) && valsArray.length > 0
            ? valsArray[valsArray.length - 1]
            : null;
        for (let prop in varSchema) {
            const type = varSchema[prop].type.toLowerCase();

            if (!shouldValueBeStored(type)) {
                // console.log('Not storing value for type: ', type);
                continue;
            }

            let isDefault = true;
            switch (type) {
                case 'video':
                case 'image':
                case 'audio':
                    // These need formatting properly!
                    // Priority 1: Use the feature variable's seeded vals[0] entry as the default.
                    // This is the admin-configured default for the slot (e.g. "intro-whoosh.ogg"),
                    // and is more reliable than valSchema.default which may be stale.
                    const seederValsEntry = node?.varSchema?.vals?.[0]?.[prop];
                    let rawDefault;
                    if (seederValsEntry != null && seederValsEntry !== '') {
                        rawDefault = seederValsEntry;
                        // Capture outer-entry isDefault from the seeder vals
                        const outerIsDefault = node?.varSchema?.vals?.[0]?.isDefault;
                        if (outerIsDefault !== undefined) isDefault = outerIsDefault;
                    } else {
                        // Priority 2: valSchema default (may be stale if set via SeederEditor import)
                        rawDefault = (varSchema[prop].hasOwnProperty('default') || varSchema[prop].hasOwnProperty('defaultVal')) ? (varSchema[prop].default ?? varSchema[prop].defaultVal) : null;
                    }
                    // The schema default can be stored as a full file-object {filename, isDefault, ...}
                    // (as set by SeederEditor) OR as a bare filename string. Normalise to a string.
                    let filename = (rawDefault && typeof rawDefault === 'object') ? rawDefault.filename ?? null : rawDefault;
                    if (rawDefault && typeof rawDefault === 'object' && rawDefault.isDefault !== undefined) {
                        isDefault = rawDefault.isDefault;
                    }
                    /* if (props.isFullPreview === true) {
                        // In full preview mode the filename has already been formatted to an object
                        newValObj[prop] = filename;
                    } else { */
                        if (filename === null || filename === '' || typeof filename === 'undefined') {
                            // do we have filename data in the first entry in the valsArray?
                            if (valsArray.length > 0) {
                                const firstEntry = valsArray[0];
                                if (firstEntry && firstEntry[prop]) {
                                    filename = firstEntry[prop].filename;
                                    isDefault = firstEntry[prop].isDefault ?? isDefault;
                                }
                            }
                        }
                        const fileObj = {
                            filename: filename,
                            isDefault: isDefault,
                            originalFile: filename,
                            type: type,
                            srcUrl: (typeof filename === 'undefined' || filename === null || filename === '') ? null :
                                getFeatureAssetBaseUrl(isDefault)
                                + featureData.defaultAssetFolder 
                                + '/' + CUSTOMISED_URL_MAP[type].dir + '/' 
                                + filename,
                            
                            /* filesRoute + '/' + CUSTOMISED_ASSETS_BASE_URL + '/' 
                                    + props.featureData.feature.defaultAssetFolder 
                                    + '/' + CUSTOMISED_URL_MAP[type].dir + '/' 
                                    + filename */
                        };
                        newValObj[prop] = fileObj;
                    // }
                    break;
                default:
                    if (varSchema[prop].hasOwnProperty('default') || varSchema[prop].hasOwnProperty('defaultVal')) {
                        newValObj[prop] = varSchema[prop].default ?? varSchema[prop].defaultVal;
                    } else if (templateEntry && Object.prototype.hasOwnProperty.call(templateEntry, prop)) {
                        const templateValue = templateEntry[prop];
                        newValObj[prop] = (templateValue && typeof templateValue === 'object' && !Array.isArray(templateValue))
                            ? { ...templateValue }
                            : templateValue;
                    } else {
                        newValObj[prop] = null;
                    }
            }
        }
        valsArray.push(newValObj);
        if (node) {
            undoRedoHook.recordCustomisationValueAdded(node, newValObj, valsArray);
            triggerSaveFeatureData(node?.featureData?._id);
        }
        console.log('Added val: ', valsArray, newValObj);
        // setFeatureState({ ...props.customisationVars });
        forceUpdateFunc?.();
    // }
}

export const removeVal = (valsArray, index, forceUpdateFunc = null, node = null) => {
    // console.log('Remove val: ', valsArray, index, objRef);
    if (valsArray.length > 1) {
        /* if (props.handleRemovedFile) {
            props.handleRemovedFile(valsArray[index]);
        } */
        // valsArray.splice(index, 1);
        
        // create a new array instance so we can make sure we see the correct data next render
        /* const oldArray = objRef.propRef[objRef.prop];
        const newArray = [];
        for (let i = 0; i < oldArray.length; i++) {
            if (i !== index) {
                newArray.push({...oldArray[i]});
            }
        } */
        // Lets force this stubborn component to update!
        // The component should be using useEffect really, but what if it isn't implemented very well in it?
        // objRef.propRef[objRef.prop] = [];
        // setFeatureState({ ...props.customisationVars });
        // Above forces a re-render where there are no vals, so the components will vanish for a split second
        // The timeout below brings em back (looking correct)
        /* setTimeout(
            () => {
                objRef.propRef[objRef.prop] = newArray;
                setFeatureState({ ...props.customisationVars });
            },
            10
        ) */
        // console.log('Val removed: ', valsArray, index, props.customisationVars);

        // objRef.propRef[objRef.prop] = newArray;
        if (node) {
            undoRedoHook.recordCustomisationValueDeleted(node, valsArray[index], valsArray, index);
        }
        valsArray.splice(index, 1);
        if (node) {
            triggerSaveFeatureData(node?.featureData?._id);
        }
        forceUpdateFunc?.();
    }
}

export const convertUniqueFilenameToOriginal = (uniqueFilename) => {
    if (!uniqueFilename || typeof uniqueFilename !== 'string') return uniqueFilename ?? '';
    const extension = uniqueFilename.split('.').pop();
    const nameWithoutExt = uniqueFilename.substring(0, uniqueFilename.lastIndexOf('.')) || uniqueFilename;
    const originalName = nameWithoutExt.split('__')[0];
    return originalName + '.' + extension;
}
